import React, { FC, useCallback } from 'react';
import { Controller, useForm } from 'react-hook-form';

import * as yup from 'yup';

import { yupResolver } from '@hookform/resolvers/yup';
import Typography from '@mui/material/Typography';

import { useStake } from '@services/contract/bonds/rewardPool.contract';
import {
  useApprove,
  useBalance,
} from '@services/contract/common/erc1155.contract';

import { useAuth } from '@contexts/common/AuthorizationContext';
import { modalActions } from '@contexts/common/ModalContext';

import useBondsInfo from '@hooks/bonds/useBondsInfo';

import { modifyEvent } from '@helpers/common/form.helpers';

import { formatBNWithCommas, toBN } from '@utils/formaters.utils';

import { amountYup } from '@validators/vaults/common.validators';

import Button from '@components/common/Button';
import Input from '@components/common/Input';
import ModalWrapper from '@components/common/Modal/ModalWrapper';
import SkeletonLoader from '@components/common/SkeletonLoader';
import SwitchChainButton from '@components/common/SwitchChainButton';

import { BONDS_STAKE_DESCRIPTION } from '@constants/bonds/common.constants';
import { NFT_BONDS, REWARD_POOL } from '@constants/bonds/contracts.constants';
import {
  DEFAULT_CRITERIA_MODE,
  DEFAULT_REVALIDATE_MODE,
  FieldNames,
} from '@constants/common/form.constants';
import { ONLY_POSITIVE_INTEGERS } from '@constants/common/regex.constants';
import { Size } from '@constants/common/ui.constants';

import useStyles from './styles.module';

type FormValues = {
  [FieldNames.Amount]: string;
};

type BondsInfo = NonNullable<ReturnType<typeof useBondsInfo>['data']>;

const StakeBondsModalContent: FC<{ bondsInfo: BondsInfo }> = ({
  bondsInfo,
}) => {
  const { chainId } = bondsInfo;
  const { classes } = useStyles();
  const { account } = useAuth();

  const {
    data: balance,
    isPending: isPendingBalance,
    refetch: refetchBalance,
  } = useBalance({ nftAddress: NFT_BONDS, chainId });

  const {
    isApproved,
    approve,
    isPendingApprove,
    refetch: refetchApprove,
  } = useApprove({ spender: REWARD_POOL, nftAddress: NFT_BONDS, chainId });

  const handleUpdate = async () => {
    await Promise.all([refetchBalance(), refetchApprove()]);
    modalActions.hide();
  };

  const { stake, isPending: isPendingStake } = useStake(chainId, handleUpdate);

  const validationSchema = yup.object().shape({
    amount: amountYup(toBN(balance)),
  });

  const {
    control,
    handleSubmit,
    trigger,
    setValue,
    formState: { isValid },
  } = useForm<FormValues>({
    mode: DEFAULT_CRITERIA_MODE,
    reValidateMode: DEFAULT_REVALIDATE_MODE,
    resolver: yupResolver(validationSchema),
    defaultValues: { [FieldNames.Amount]: '' },
  });

  const onSubmit = async (formData: FormValues) => {
    await trigger();
    // Bernard's useStake fires writeContract synchronously (no awaitable);
    // the modal closes via handleUpdate on the contract's onSuccess.
    stake(BigInt(formData[FieldNames.Amount]));
  };

  const onMaxHandler = useCallback(() => {
    if (!account || !balance || toBN(balance).isZero()) return;
    setValue(FieldNames.Amount, toBN(balance).toFixed(0), {
      shouldValidate: true,
    });
  }, [balance, account, setValue]);

  const isDisableStake = !balance || toBN(balance).isZero() || !isValid;

  return (
    <ModalWrapper title="Stake Bonds">
      <form className={classes.container} onSubmit={handleSubmit(onSubmit)}>
        <div className={classes.noticeCard}>
          <Typography
            variant="body2"
            color={(theme) => theme.palette.custom.text.light}
            align="center"
          >
            {BONDS_STAKE_DESCRIPTION}
          </Typography>
        </div>

        <div className={classes.balanceInfo}>
          {isPendingBalance ? (
            <SkeletonLoader
              className={classes.skeleton}
              classNameWrap={classes.skeleton}
            />
          ) : (
            <Typography
              variant="caption1"
              color={(theme) => theme.palette.custom.text.accent}
            >
              {`Wallet Balance: ${formatBNWithCommas(balance, 0)}`}
            </Typography>
          )}
          <div className={classes.inputAndButton}>
            <Controller
              control={control}
              name={FieldNames.Amount}
              render={({
                field: { value, onBlur, onChange },
                fieldState: { error },
              }) => (
                <Input
                  value={value}
                  onBlur={onBlur}
                  onChange={(e) =>
                    onChange(modifyEvent(e, ONLY_POSITIVE_INTEGERS))
                  }
                  placeholder="0"
                  onMax={onMaxHandler}
                  inputError={error?.message}
                  disabled={!account}
                />
              )}
            />
            <SwitchChainButton chainId={chainId} size={Size.Large}>
              {isApproved ? (
                <Button
                  buttonType="submit"
                  disabled={isDisableStake}
                  isLoading={isPendingStake}
                >
                  Stake
                </Button>
              ) : (
                <Button onClick={approve} isLoading={isPendingApprove}>
                  Approve
                </Button>
              )}
            </SwitchChainButton>
          </div>
        </div>
      </form>
    </ModalWrapper>
  );
};

const StakeBondsModal: FC = () => {
  const { data: bondsInfo } = useBondsInfo();

  if (!bondsInfo) return null;

  return <StakeBondsModalContent bondsInfo={bondsInfo} />;
};

export default StakeBondsModal;
